import { ACCOUNT_MANAGEMENT_LE_ADMIN } from '../../../constants/roles';

import type { MemberRole, MemberRolesForEntity } from './constants';

/**
 * Roles whose grant or revocation requires a step-up 2FA challenge. Mirrors
 * `organizations.StepUpGatedRoles` in identity/account-management, which
 * re-derives the requirement authoritatively; these values only decide when the
 * Workspace UI prompts.
 */
export const STEP_UP_GATED_ROLES: readonly MemberRole[] = [
  ACCOUNT_MANAGEMENT_LE_ADMIN,
  'account_management/legal_entity:viewer',
  'cdp/legal_entity:cdp_entity_admin_role',
  'shared/legal_entity:coinbiz_admin',
  // Frontend format, not the bare WORKSPACE_ORG_ADMIN in constants/roles.ts: this list is compared
  // against legalEntityRoles[], which carries frontend-format strings, while the bare name appears
  // on the top-level roles[] array, which canManageMembers.ts reads as `orgRoles`. No Workspace UI grants the role
  // yet, so it cannot match today; it is listed so isGatedRoleMutation challenges as soon as one does.
  'account_management/organization:workspace_org_admin',
];

const GATED_ROLES = new Set<MemberRole>(STEP_UP_GATED_ROLES);

// Stands in for the one legal entity isSingleEntityGatedRoleMutation compares within.
const SINGLE_LEGAL_ENTITY = '';

export function hasGatedRole(roles: readonly MemberRole[]): boolean {
  return roles.some((role) => GATED_ROLES.has(role));
}

function setsAreEqual(current: ReadonlySet<string>, next: ReadonlySet<string>): boolean {
  if (current.size !== next.size) {
    return false;
  }
  for (const key of current) {
    if (!next.has(key)) {
      return false;
    }
  }
  return true;
}

function rolesByEntity(
  legalEntityRoles: readonly MemberRolesForEntity[],
): Map<string, Set<MemberRole>> {
  const byEntity = new Map<string, Set<MemberRole>>();
  for (const { legalEntityId, roles } of legalEntityRoles) {
    const entityRoles = byEntity.get(legalEntityId) ?? new Set<MemberRole>();
    for (const role of roles) {
      entityRoles.add(role);
    }
    byEntity.set(legalEntityId, entityRoles);
  }
  return byEntity;
}

/**
 * True when the save changes the roles on a legal entity that holds a gated role
 * before or after the edit. A legal entity added or dropped entirely counts as a
 * change.
 *
 * Mirrors `legalEntityRoleChangeRequiresStepUp` and
 * `legalEntityBatchRoleChangeRequiresStepUp` in identity/account-management. The
 * backend does not diff gated roles: it requires a proof token when a legal
 * entity in the request carries a gated role, either submitted or already held.
 * Editing only the ungated roles of an admin therefore still needs step-up.
 */
export function isGatedRoleMutation(
  currentLegalEntityRoles: readonly MemberRolesForEntity[],
  nextLegalEntityRoles: readonly MemberRolesForEntity[],
): boolean {
  const currentByEntity = rolesByEntity(currentLegalEntityRoles);
  const nextByEntity = rolesByEntity(nextLegalEntityRoles);
  const legalEntityIds = [...new Set([...currentByEntity.keys(), ...nextByEntity.keys()])];

  return legalEntityIds.some((legalEntityId) => {
    const currentRoles = currentByEntity.get(legalEntityId) ?? new Set<MemberRole>();
    const nextRoles = nextByEntity.get(legalEntityId) ?? new Set<MemberRole>();
    return (
      !setsAreEqual(currentRoles, nextRoles) &&
      (hasGatedRole([...currentRoles]) || hasGatedRole([...nextRoles]))
    );
  });
}

/** isGatedRoleMutation where both role sets belong to the same legal entity. */
export function isSingleEntityGatedRoleMutation(
  currentRoles: readonly MemberRole[],
  nextRoles: readonly MemberRole[],
): boolean {
  return isGatedRoleMutation(
    [{ legalEntityId: SINGLE_LEGAL_ENTITY, roles: [...currentRoles] }],
    [{ legalEntityId: SINGLE_LEGAL_ENTITY, roles: [...nextRoles] }],
  );
}
